import { memo, useCallback } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { useOnboardingPreferencesStore } from '~/components/v2/store/features/onboarding/useOnboardingPreferencesStore';
import { toAnimatedOnboardingUploadFiles } from '~/components/WorkspaceOnboardingForm/toAnimatedOnboardingUploadFiles';
import { useSubmitOnboardingUpload } from '~/components/WorkspaceOnboardingForm/useSubmitOnboardingUpload';
import { WorkspaceAnimatedOnboardingSky } from '~/components/WorkspaceOnboardingForm/WorkspaceAnimatedOnboardingSky';
import { WorkspaceAnimatedOnboardingUploadStep } from '~/components/WorkspaceOnboardingForm/WorkspaceAnimatedOnboardingUploadStep';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

export const WorkspaceAnimatedOnboardingUploadPrompt = memo(() => {
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const clearShowFirstUploadPrompt = useOnboardingPreferencesStore((state) => state.clearShowFirstUploadPrompt);
  const clearPendingAnimatedUploadStep = useOnboardingPreferencesStore((state) => state.clearPendingAnimatedUploadStep);
  const { submitOnboardingUpload } = useSubmitOnboardingUpload();

  const handleSkip = useCallback(() => {
    clearPendingAnimatedUploadStep();

    if (!workspaceId) {
      return;
    }

    clearShowFirstUploadPrompt(workspaceId);
  }, [clearPendingAnimatedUploadStep, clearShowFirstUploadPrompt, workspaceId]);

  const handleConfirmPick = useCallback(
    (files: readonly File[]) => {
      clearPendingAnimatedUploadStep();
      void submitOnboardingUpload({
        acceptedFiles: toAnimatedOnboardingUploadFiles([...files]),
        category: 'agent_onboarding',
      }).catch((error: unknown) => {
        reportErrorToBugsnag({
          error,
          context: 'Failed to submit pending onboarding upload',
          metadata: { data: { workspaceId } },
        });
      });
    },
    [clearPendingAnimatedUploadStep, submitOnboardingUpload, workspaceId],
  );

  return (
    <div className="fixed inset-0 z-portal overflow-hidden" data-theme="dark">
      <WorkspaceAnimatedOnboardingSky />
      <WorkspaceAnimatedOnboardingUploadStep onConfirmPick={handleConfirmPick} onSkip={handleSkip} />
    </div>
  );
});

WorkspaceAnimatedOnboardingUploadPrompt.displayName = 'WorkspaceAnimatedOnboardingUploadPrompt';
